import React, { useEffect, useState } from 'react'
import { Trash2, Plus, Pencil, Unlock, Lock, Eye, RefreshCcw, DollarSign, Filter, UserRoundSearch } from 'lucide-react'
import { formatDate } from '@/utils/dates-util'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CustomDataTable } from '@/components/table/custom-data-table'
import { formatted_user_role, formatted_user_status, user_role, user_status, User, UserProps } from '@/entities/user'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/store/auth'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { toast } from '@/hooks/use-toast'
import { UserAvatar } from '@/components/ui_custom/user-avatar'
import { ColumnDef } from '@tanstack/react-table'
import { showConfirm } from '@/lib/alerts'
import { useLoader } from '@/contexts/LoaderContext'
import { Button } from '@/components/ui/button'
import { getUserPlanBadgeStyle, getUserRoleBadgeStyle, getUserStatusBadgeStyle } from '@/utils/badges-util'
import { CreateUserDialog } from '@/components/dialogs/create-user-dialog'
import { PageHeader } from '@/components/common/page-header'
import { PLANS_DATA } from '@/constants/hotmart-plans'
import { UserDetailsDialog } from '@/components/dialogs/user-details-dialog'
import { FilterDialog } from '@/components/dialogs/filter-dialog'
import { FilterChips } from '@/components/ui/filter-chips'
import { useAppliedFilters } from '@/hooks/use-applied-filters'
import { UserHotmartDataDialog } from '@/components/dialogs/user-hotmart-data-dialog'

const loc = '@/pages/Users'

export default function UserManagement() {
  const { user } = useAuthStore()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const [users, setUsers] = useState([])
  const [usersAmount, setUsersAmount] = useState(0)
  const [selectedUser, setSelectedUser] = useState<UserProps | User>({})
  const [isCreateUserOpen, setIsCreateUserOpen] = useState<boolean>(false)
  const [isUserHotmartSearchOpen, setIsUserHotmartSearchOpen] = useState<boolean>(false)
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState<boolean>(false)
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const [hotmartData, setHotmartData] = useState<unknown>(null)
  const { appliedFilters, filterChips, applyFilters, removeFilter, clearAllFilters } = useAppliedFilters()

  const columns: ColumnDef<UserProps>[] = [
    {
      accessorKey: 'name',
      header: '',
      cell: ({ row }) => <UserAvatar name={row.original.name || ''} size="md" />
    },
    {
      accessorKey: 'name',
      header: 'Nome',
      cell: ({ row }) => <span className="text-foreground dark:text-white">{row.original.name}</span>
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <span className="text-foreground dark:text-white">{row.original.email}</span>
    },
    {
      accessorKey: 'lastLoginAt',
      header: 'Último acesso',
      cell: ({ row }) => <span className="text-foreground dark:text-white">{row.original.lastLoginAt ? formatDate(new Date(row.original.lastLoginAt || new Date())) : '-'}</span>
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <span className={`rounded-md py-1 px-2 text-center ${getUserStatusBadgeStyle(row.original.status || user_status.ALLOWED)}`}>{formatted_user_status[row.original.status || user_status.ALLOWED] || '-'}</span>
    },
    {
      accessorKey: 'role',
      header: 'Função',
      cell: ({ row }) => <span className={`rounded-md py-1 px-2 text-center ${getUserRoleBadgeStyle(row.original.role || user_role.CUSTOMER)}`}>{formatted_user_role[row.original.role || user_role.CUSTOMER] || '-'}</span>
    },
    {
      accessorKey: 'planId',
      header: 'Plano',
      cell: ({ row }) => (
        <span className={`min-w-60 rounded-md py-1 px-2 text-center ${getUserPlanBadgeStyle(row.original.planId || 0)}`}>
          {row.original.planId
            ? PLANS_DATA?.concat([
                {
                  id: 462895,
                  name: 'Mae Nutrida',
                  value: 20
                }
              ]).find(plan => plan.id === row.original.planId)?.name
            : 'Sem Plano'}
        </span>
      )
    },
    {
      accessorKey: 'userId',
      header: 'Tipo cadastro',
      cell: ({ row }) => <span className={`rounded-md py-1 px-2 text-center text-white ${row.original?.userId ? 'bg-gray-400' : 'bg-orange-500'}`}>{row.original?.userId ? 'Manual' : 'Hotmart'}</span>
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const item = row.original
        return (
          <div className="flex justify-end gap-x-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={async () => {
                    _handleResetPlanBalance(item.ref || '')
                  }}
                  disabled={item.status != user_status.ALLOWED || !item.planId}
                  size="sm"
                >
                  <span className="sr-only">Resetar saldo do plano</span>
                  <div className="relative w-6 h-6">
                    <RefreshCcw className="absolute w-6 h-6" />
                    <DollarSign className="absolute w-3 h-3 top-[6px] left-[6px]" />
                  </div>
                </Button>
              </TooltipTrigger>

              <TooltipContent side="top" className="bg-primary text-white text-xs">
                Resetar saldo do plano
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={async () => {
                    _handleChangeUserStatus(item.ref || '', item.status == user_status.ALLOWED ? user_status.BLOCKED : user_status.ALLOWED)
                  }}
                  size="sm"
                >
                  <span className="sr-only">{item.status == user_status.ALLOWED ? 'Bloquear' : 'Desbloquear'}</span>
                  {item.status == user_status.ALLOWED ? <Lock className="size-4" /> : <Unlock className="size-4" />}
                </Button>
              </TooltipTrigger>

              <TooltipContent side="top" className="bg-primary text-white text-xs">
                {item.status == user_status.ALLOWED ? 'Bloquear' : 'Desbloquear'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={async () => {
                    _handleShowUserDetails(item.ref || '', item.email || '')
                  }}
                  size="sm"
                >
                  <span className="sr-only">Ver detalhes</span>
                  <Eye className="size-4" />
                </Button>
              </TooltipTrigger>

              <TooltipContent side="top" className="bg-primary text-white text-xs">
                Ver detalhes
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  onClick={async () => {
                    setSelectedUser(item)
                    setIsCreateUserOpen(true)
                  }}
                  size="sm"
                >
                  <span className="sr-only">Editar</span>
                  <Pencil className="size-4" />
                </Button>
              </TooltipTrigger>

              <TooltipContent side="top" className="bg-primary text-white text-xs">
                Editar
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  onClick={() => {
                    _handleDeleteUser(item.ref || '')
                  }}
                  className="bg-red-700 text-white hover:bg-red-700/70 hover:brightness-125"
                  size="sm"
                >
                  <span className="sr-only">Deletar</span>
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>

              <TooltipContent side="top" className="bg-red-700 text-white text-xs">
                Deletar
              </TooltipContent>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  const _handleDeleteUser = async (UserRef: string) => {
    try {
      const { isConfirmed } = await showConfirm('Deseja deletar o usuário?')
      if (!isConfirmed) return

      showLoader()

      const response = await api.delete('/', {
        data: {
          action: 'user',
          ref: UserRef,
          userRef: user?.ref
        }
      })

      if (response.data?.statusCode === HTTP_STATUS_CODE.OK) {
        toast({ variant: 'success', title: 'Sucesso', description: 'Usuário deletado com sucesso.' })
        await _fetchData(true)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível deletar o usuário.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível deletar o usuário.' })
      console.error(`Unhandled error at ${loc}._handleDeleteUser. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _handleShowUserDetails = async (UserRef: string, userEmail: string) => {
    try {
      showLoader()
      setIsUserDetailsOpen(true)
      setSelectedUser(users.find((user: UserProps) => user.ref === UserRef) || ({} as UserProps))

      const response = await api.get('/', {
        params: {
          action: 'user-hotmart-data',
          email: userEmail,
          userRef: user?.ref
        }
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setHotmartData(response.data.body[0])
      } else {
        toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível ver os detalhes do usuário.' })
      }
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível ver os detalhes do usuário.' })
      console.error(`Unhandled error at ${loc}._handleShowUserDetails. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _fetchData = async (refetch: boolean) => {
    try {
      if (!refetch && users.length) return

      showLoader()
      const response = await api.get('/', {
        params: {
          action: 'user',
          userRef: user?.ref,
          filters: appliedFilters
        }
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        if (user?.role != user_role.ADMIN) {
          setUsers(response.data.body[0]?.filter((user: UserProps) => user.role !== user_role.ADMIN))
          setUsersAmount(response.data.body[1])
        } else {
          setUsers(response.data.body[0])
          setUsersAmount(response.data.body[1])
        }
      } else {
        toast({
          variant: 'destructive',
          title: 'Ops ...',
          description: response?.data?.statusMessage || 'Erro ao enviar mensagem!'
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      hideLoader()
    }
  }

  const handleRemoveFilter = (key: string) => {
    removeFilter(key)
    _fetchData(true)
  }

  const handleClearAllFilters = () => {
    clearAllFilters()
    _fetchData(true)
  }

  const _handleChangeUserStatus = async (UserRef: string, status: user_status) => {
    try {
      const { isConfirmed } = await showConfirm(`Deseja ${status != user_status.ALLOWED ? 'bloquear' : 'desbloquear'} o usuário?`)
      if (!isConfirmed) return

      showLoader()

      const response = await api.put('/', {
        action: 'user-status',
        ref: UserRef,
        userRef: user?.ref,
        status: status
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        toast({ variant: 'success', title: 'Sucesso', description: 'Status do usuário alterado com sucesso.' })
        await _fetchData(true)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível alterar o status do usuário.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível alterar o status do usuário.' })
      console.error(`Unhandled error at ${loc}._handleChangeUserStatus. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _handleResetPlanBalance = async (UserRef: string) => {
    try {
      const { isConfirmed } = await showConfirm(`Deseja resetar o saldo do plano do usuário?`)
      if (!isConfirmed) return

      showLoader()

      const response = await api.put('/', {
        action: 'user-plan-balance',
        ref: UserRef,
        userRef: user?.ref
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        toast({ variant: 'success', title: 'Sucesso', description: 'Saldo do plano resetado com sucesso.' })
        await _fetchData(true)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível resetar o saldo do plano.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível resetar o saldo do plano.' })
      console.error(`Unhandled error at ${loc}._handleResetPlanBalance. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  useEffect(() => {
    _fetchData(true)
  }, [appliedFilters])

  useEffect(() => {
    _fetchData(false)
  }, [])

  return (
    <>
      <div className="mx-0">
        <PageHeader title="Gerenciamento de Usuários" />

        <div className="rounded-xl shadow-sm mt-5">
          <CustomDataTable columns={columns} data={users} loading={isLoading} filterKey={{ title: 'Nome / Email', value: ['name', 'email'] }} amount={usersAmount}>
            <div className="w-full flex flex-row gap-y-2 space-between items-end smAndDown:flex-col smAndDown:gap-y-4 smAndDown:items-center">
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      onClick={() => {
                        setIsCreateUserOpen(true)
                      }}
                      className="smAndDown:w-full"
                      disabled={isLoading}
                    >
                      <Plus className="size-5" />
                      Criar Usuário
                    </Button>
                  </TooltipTrigger>

                  <TooltipContent side="top" className="bg-primary text-white text-xs mdAndUp:hidden">
                    Criar Usuário
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="min-w-auto flex-1">
                <FilterChips filters={filterChips} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} className="mt-4" />
              </div>
              <div className="flex gap-x-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      onClick={() => {
                        setIsUserHotmartSearchOpen(true)
                      }}
                      className="smAndDown:w-full"
                      disabled={isLoading}
                    >
                      <UserRoundSearch className="size-5" />
                    </Button>
                  </TooltipTrigger>

                  <TooltipContent side="top" className="bg-primary text-white text-xs">
                    Buscar Usuário na Hotmart
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      onClick={() => {
                        setIsFilterOpen(true)
                      }}
                      className="smAndDown:w-full"
                      disabled={isLoading}
                    >
                      <Filter className="size-5" />
                    </Button>
                  </TooltipTrigger>

                  <TooltipContent side="top" className="bg-primary text-white text-xs">
                    Filtrar Usuários
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CustomDataTable>
        </div>
      </div>

      {isCreateUserOpen && (
        <CreateUserDialog
          open={isCreateUserOpen}
          userProp={selectedUser}
          cb={() => {
            _fetchData(true)
            setSelectedUser({} as UserProps)
            setIsCreateUserOpen(false)
          }}
        />
      )}

      {isUserDetailsOpen && (
        <UserDetailsDialog
          open={isUserDetailsOpen}
          user={selectedUser}
          hotmartData={hotmartData}
          isLoading={isLoading}
          cb={() => {
            setSelectedUser({} as UserProps)
            setIsUserDetailsOpen(false)
          }}
        />
      )}

      {isFilterOpen && (
        <FilterDialog
          open={isFilterOpen}
          close={() => {
            setIsFilterOpen(false)
            _fetchData(true)
          }}
          filters={appliedFilters}
          handleRemoveFilter={handleRemoveFilter}
          handleClearAllFilters={handleClearAllFilters}
          handleApplyFilters={filters => {
            applyFilters(filters)
            setIsFilterOpen(false)
          }}
        />
      )}

      {isUserHotmartSearchOpen && (
        <UserHotmartDataDialog
          open={isUserHotmartSearchOpen}
          cb={() => {
            setIsUserHotmartSearchOpen(false)
          }}
        />
      )}
    </>
  )
}
